import { useCallback } from "react";
import { success } from "@";
import {
  useDownloadPdfMutation,
  useMarkAsPrintedMutation,
  useReturnToDriverMutation,
  useUnassignDriverMutation,
  useUpdateStatusMutation,
} from "../api/mutations";
import {
  acceptOrdersConfirmDialog,
  markAsPrintedConfirmDialog,
  returnAcceptedVerificationConfirmDialog,
  unassignOrdersConfirmDialog,
} from "../lib/helpers.ts";
import type { VerifySearchValues } from "../types.ts";

interface UseBulkActionsParams {
  selectedIds: number[];
  selectingAll: boolean;
  draftValues: VerifySearchValues;
  acceptOrdersMutation: ReturnType<typeof useUpdateStatusMutation>;
  clearSelection: () => void;
}

export interface UseBulkActionsReturn {
  handleAcceptOrders: () => Promise<void>;
  handleUnassignDriver: () => Promise<void>;
  handleReturnToDriver: () => Promise<void>;
  handleMarkAsPrinted: () => Promise<void>;
  handleDownloadPdf: () => Promise<void>;
  isUnassigning: boolean;
  isReturning: boolean;
  isMarkingAsPrinted: boolean;
  isDownloadingPdf: boolean;
  isAcceptingOrders: boolean;
}

export const useBulkActions = ({
  selectedIds,
  selectingAll,
  draftValues,
  acceptOrdersMutation,
  clearSelection,
}: UseBulkActionsParams): UseBulkActionsReturn => {
  const unassignDriverMutation = useUnassignDriverMutation();
  const returnToDriverMutation = useReturnToDriverMutation();
  const markAsPrintedMutation = useMarkAsPrintedMutation();
  const downloadPdfMutation = useDownloadPdfMutation();

  const handleAcceptOrders = useCallback(async () => {
    const confirmed = await acceptOrdersConfirmDialog();
    if (!confirmed) return;

    await acceptOrdersMutation.mutateAsync({
      verificationStagesIds: selectedIds,
      isAccepted: true,
      comment: "",
      searchValues: draftValues,
      selectingAll,
    });
    await success("تم تاكيد الطلبات بنجاح");
    clearSelection();
  }, [
    selectedIds,
    acceptOrdersMutation,
    clearSelection,
    draftValues,
    selectingAll,
  ]);

  const handleUnassignDriver = useCallback(async () => {
    const confirmed = await unassignOrdersConfirmDialog();
    if (!confirmed) return;

    await unassignDriverMutation.mutateAsync({
      verificationStagesIds: selectedIds,
      searchValues: draftValues,
      selectingAll,
    });
    await success("تم إلغاء إسناد الطلبات بنجاح");
    clearSelection();
  }, [
    selectedIds,
    unassignDriverMutation,
    clearSelection,
    draftValues,
    selectingAll,
  ]);

  const handleReturnToDriver = useCallback(async () => {
    const confirmed = await returnAcceptedVerificationConfirmDialog();
    if (!confirmed) return;

    await returnToDriverMutation.mutateAsync({
      verificationIds: selectedIds,
      searchValues: draftValues,
      selectingAll,
    });
    await success("تم إلغاء توثيق الطلبات بنجاح");
    clearSelection();
  }, [
    selectedIds,
    returnToDriverMutation,
    clearSelection,
    draftValues,
    selectingAll,
  ]);

  const handleMarkAsPrinted = useCallback(async () => {
    const confirmed = await markAsPrintedConfirmDialog();
    if (!confirmed) return;

    await markAsPrintedMutation.mutateAsync({
      verificationStaesIds: selectedIds,
      searchValues: draftValues,
      selectingAll,
    });
    await success("تم تحديد الطلبات كمطبوعة بنجاح");
    clearSelection();
  }, [
    selectedIds,
    markAsPrintedMutation,
    clearSelection,
    draftValues,
    selectingAll,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    await downloadPdfMutation.mutateAsync({
      verificationStaesIds: selectedIds,
      searchValues: draftValues,
      selectingAll,
    });

    handleMarkAsPrinted();
  }, [
    selectedIds,
    downloadPdfMutation,
    draftValues,
    selectingAll,
    handleMarkAsPrinted,
  ]);

  return {
    handleAcceptOrders,
    handleUnassignDriver,
    handleReturnToDriver,
    handleMarkAsPrinted,
    handleDownloadPdf,
    isUnassigning: unassignDriverMutation.isPending,
    isReturning: returnToDriverMutation.isPending,
    isMarkingAsPrinted: markAsPrintedMutation.isPending,
    isDownloadingPdf: downloadPdfMutation.isPending,
    isAcceptingOrders: acceptOrdersMutation.isPending,
  };
};
